import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils';
import ExpositionsView from './ExpositionsView';
import EventsView from './EventsView';
import EventDetailsView from './EventDetailsView';
import CatalogView from './CatalogView';
import ArtCatalogView from './ArtCatalogView';
import AccessoriesCatalogView from './AccessoriesCatalogView';
import CallbackForm from './CallbackForm';
import ItemDetailsView from './ItemDetailsView';
import CustomPageView from './CustomPageView';
import { API_BASE_URL } from '../config';

function FloatingMenu({
  onMenuItemClick,
  onTeleportToItem,
  onTeleportToRoom,
  onShowConfirmation,
  currentShowroomId,
  backgroundLoadingProgress,
  roomReadiness,
  externalNavigation
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentView, setCurrentView] = useState('menu');
  const [navigationStack, setNavigationStack] = useState([]);
  const [viewData, setViewData] = useState(null);
  const [customPages, setCustomPages] = useState([]);
  const scrollContainerRef = useRef(null);

  // Fetch custom pages on component mount
  useEffect(() => {
    fetchCustomPages();
  }, []);

  // Handle external navigation requests
  useEffect(() => {
    if (externalNavigation) {
      const { action, view, data } = externalNavigation;
      if (action === 'open') {
        setIsOpen(true);
        if (view) {
          setCurrentView(view);
          setViewData(data || null);
        }
      }
    }
  }, [externalNavigation]);

  const fetchCustomPages = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/pages`);
      if (response.ok) {
        const pages = await response.json();
        setCustomPages(pages);
      }
    } catch (error) {
      console.error('Error fetching custom pages:', error);
    }
  };

  const toggleMenu = () => {
    if (isOpen) {
      setIsOpen(false);
      setCurrentView('menu');
      setNavigationStack([]);
      setViewData(null);
    } else {
      setIsOpen(true);
    }
  };

  const navigateToView = (view, data = null) => {
    if (currentView !== 'menu') {
      setNavigationStack(prev => [...prev, { view: currentView, data: viewData }]);
    }
    setCurrentView(view);
    setViewData(data);

    // Scroll to top when navigating to new view
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 50);
  };

  const navigateBack = () => {
    if (navigationStack.length > 0) {
      const previous = navigationStack[navigationStack.length - 1];
      setNavigationStack(prev => prev.slice(0, -1));
      setCurrentView(previous.view);
      setViewData(previous.data);
    } else {
      setCurrentView('menu');
      setViewData(null);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setCurrentView('menu');
    setNavigationStack([]);
    setViewData(null);
  };

  const handleTeleportToItem = (...args) => {
    onTeleportToItem(...args);
    handleClose(); // Close panel after teleport
  };

  const handleTeleportToRoom = (...args) => {
    onTeleportToRoom(...args);
    handleClose(); // Close panel after teleport
  };

  const handleShowConfirmationWrapper = (title, message, onConfirm) => {
    if (onShowConfirmation && typeof onShowConfirmation === 'function') {
      onShowConfirmation(title, message, onConfirm);
    } else {
      console.error('onShowConfirmation is not a function:', onShowConfirmation);
    }
  };


  const renderContent = () => {
    switch (currentView) {
      case 'expositions':
        return (
          <ExpositionsView 
            onTeleportToRoom={handleTeleportToRoom}
            onShowConfirmation={handleShowConfirmationWrapper}
            currentShowroomId={currentShowroomId}
            backgroundLoadingProgress={backgroundLoadingProgress}
            roomReadiness={roomReadiness}
          />
        );
      case 'events':
        return (
          <EventsView 
            onShowEventDetails={(event) => navigateToView('event-details', event)}
          />
        );
      case 'event-details':
        return (
          <EventDetailsView 
            event={viewData}
            onBack={navigateBack}
          />
        );
      case 'catalog':
        return (
          <CatalogView 
            onShowItemDetails={(item, showrooms) => navigateToView('item-details', { item, showrooms })}
            onTeleportToItem={handleTeleportToItem}
            onShowConfirmation={handleShowConfirmationWrapper}
            currentShowroomId={currentShowroomId}
          />
        );
      case 'art-catalog':
        return (
          <ArtCatalogView 
            onShowItemDetails={(item, showrooms) => navigateToView('item-details', { item, showrooms })}
            onTeleportToItem={handleTeleportToItem}
            onShowConfirmation={handleShowConfirmationWrapper}
            currentShowroomId={currentShowroomId}
            backgroundLoadingProgress={backgroundLoadingProgress}
            roomReadiness={roomReadiness}
          />
        );
      case 'accessories-catalog':
        return (
          <AccessoriesCatalogView 
            onShowItemDetails={(item, showrooms) => navigateToView('item-details', { item, showrooms })}
            onTeleportToItem={handleTeleportToItem}
            onShowConfirmation={handleShowConfirmationWrapper}
            currentShowroomId={currentShowroomId}
            backgroundLoadingProgress={backgroundLoadingProgress}
            roomReadiness={roomReadiness}
          />
        );
      case 'item-details':
        return (
          <ItemDetailsView 
            data={viewData}
            onTeleportToItem={handleTeleportToItem}
            onShowConfirmation={handleShowConfirmationWrapper}
            currentShowroomId={currentShowroomId}
            onBack={navigateBack}
          />
        );
      case 'callback':
        return <CallbackForm onClose={handleClose} />;
      case 'custom-page':
        return (
          <CustomPageView 
            pageSlug={viewData}
            onBack={navigateBack}
          />
        );
      default:
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <button
              onClick={() => navigateToView('expositions')}
              className={cn(
                "group relative",
                "bg-white/90 border border-gray-200 hover:border-gray-300",
                "rounded-lg shadow-sm hover:shadow-md",
                "p-8 transition-all duration-200",
                "flex flex-col items-center justify-center min-h-[140px]",
                "hover:bg-white"
              )}
            >
              <div className="w-8 h-8 mb-4 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                <div className="w-4 h-4 border border-gray-600 rounded-sm">
                  <div className="w-2 h-1.5 bg-gray-600 mt-0.5 mx-auto"></div>
                </div>
              </div>
              <span className="text-sm font-medium text-gray-800 text-center uppercase tracking-wide">
                {t('expositions')}
              </span>
            </button>

            <button
              onClick={() => navigateToView('events')}
              className={cn(
                "group relative",
                "bg-white/90 border border-gray-200 hover:border-gray-300",
                "rounded-lg shadow-sm hover:shadow-md",
                "p-8 transition-all duration-200",
                "flex flex-col items-center justify-center min-h-[140px]",
                "hover:bg-white"
              )}
            >
              <div className="w-8 h-8 mb-4 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                <div className="grid grid-cols-7 gap-0.5">
                  {Array.from({length: 21}).map((_, i) => (
                    <div key={i} className="w-0.5 h-0.5 bg-gray-600 rounded-full"></div>
                  ))}
                </div>
              </div>
              <span className="text-sm font-medium text-gray-800 text-center uppercase tracking-wide">
                {t('events')}
              </span>
            </button>

            <button
              onClick={() => navigateToView('art-catalog')}
              className={cn(
                "group relative",
                "bg-white/90 border border-gray-200 hover:border-gray-300",
                "rounded-lg shadow-sm hover:shadow-md",
                "p-6 transition-all duration-200",
                "flex flex-col items-center justify-center min-h-[120px]",
                "hover:bg-white"
              )}
            >
              <div className="w-8 h-8 mb-3 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                <div className="space-y-1">
                  <div className="w-4 h-0.5 bg-gray-600"></div>
                  <div className="w-3 h-0.5 bg-gray-600"></div>
                  <div className="w-4 h-0.5 bg-gray-600"></div>
                </div>
              </div>
              <span className="text-xs font-medium text-gray-800 text-center uppercase tracking-wide leading-tight">
                Предметы искусства
              </span>
            </button>
            <button
              onClick={() => navigateToView('accessories-catalog')}
              className={cn(
                "group relative",
                "bg-white/90 border border-gray-200 hover:border-gray-300",
                "rounded-lg shadow-sm hover:shadow-md",
                "p-6 transition-all duration-200",
                "flex flex-col items-center justify-center min-h-[120px]",
                "hover:bg-white"
              )}
            >
              <div className="w-8 h-8 mb-3 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                <div className="space-y-1">
                  <div className="w-4 h-0.5 bg-gray-600"></div>
                  <div className="w-2 h-0.5 bg-gray-600"></div>
                  <div className="w-3 h-0.5 bg-gray-600"></div>
                </div>
              </div>
              <span className="text-xs font-medium text-gray-800 text-center uppercase tracking-wide leading-tight">
                Магазин аксессуаров
              </span>
            </button>

            <button
              onClick={() => navigateToView('callback')}
              className={cn(
                "group relative",
                "bg-white/90 border border-gray-200 hover:border-gray-300",
                "rounded-lg shadow-sm hover:shadow-md",
                "p-8 transition-all duration-200",
                "flex flex-col items-center justify-center min-h-[140px]",
                "hover:bg-white"
              )}
            >
              <div className="w-8 h-8 mb-4 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                <div className="w-4 h-3 border border-gray-600 rounded-sm relative">
                  <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-b-2 border-transparent border-b-gray-600"></div>
                </div>
              </div>
              <span className="text-sm font-medium text-gray-800 text-center uppercase tracking-wide">
                {t('contact')}
              </span>
            </button>

            {/* Custom Pages */}
            {customPages.map(page => (
              <button
                key={page.slug}
                onClick={() => navigateToView('custom-page', page.slug)}
                className={cn(
                  "group relative",
                  "bg-white/90 border border-gray-200 hover:border-gray-300",
                  "rounded-lg shadow-sm hover:shadow-md",
                  "p-8 transition-all duration-200",
                  "flex flex-col items-center justify-center min-h-[140px]",
                  "hover:bg-white"
                )}
              >
                <div className="w-8 h-8 mb-4 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                  {page.icon ? (
                    <span className="text-lg">{page.icon}</span>
                  ) : (
                    <div className="w-4 h-4 bg-gray-600 rounded-sm"></div>
                  )}
                </div>
                <span className="text-sm font-medium text-gray-800 text-center uppercase tracking-wide">
                  {page.title}
                </span>
              </button>
            ))}
          </div>
        );
    }
  };

  return (
    <>

      {/* Backdrop */}
      {isOpen && (
        <div
          onClick={toggleMenu}
          className="fixed inset-0 z-[998] bg-black/20 backdrop-blur-sm"
        />
      )}

      {/* Interactive Panel */}
      <div
        ref={scrollContainerRef}
        className={cn(
          "fixed top-0 left-0 right-0 bottom-0 z-[999]",
          "bg-white/95 backdrop-blur-md shadow-xl",
          "transition-all duration-300 ease-out",
          "overflow-y-auto",
          isOpen ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
        )}
      >
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="relative flex items-center px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
            {/* Back button - positioned absolutely on the left */}
            {currentView !== 'menu' && (
              <button
                onClick={navigateBack}
                className="absolute left-4 sm:left-6 flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="text-sm font-medium uppercase tracking-wide">Назад</span>
              </button>
            )}
            
            {/* Logo - centered */}
            <div className="flex items-center justify-center w-full">
              <img 
                src="/art-acs.png" 
                alt="Арт & Акс Галерея" 
                className="h-8 sm:h-8 object-contain"
              />
            </div>
            
            {/* Close button - positioned absolutely on the right */}
            <button
              onClick={handleClose}
              className="absolute right-4 sm:right-6 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-8">
            {renderContent()}
          </div>
        </div>
      </div>
    </>
  );
}

export default FloatingMenu;